import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, FormControlLabel, FormHelperText, Switch } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { CheckboxCDS } from '@sharedCR/coreDesignSystem/form';
import { DeleteIcon, PlusIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  argyleReportTypeOptions,
  avmVendorTypeMap,
  createDefaultCreditBureaus,
  createEmptyFormYearRule,
  creditBureauFields,
  creditOrderActionSubtypes,
  creditPullTypeOptions,
  defaultCreditPullTypeOption,
  defaultHalcyonAuthorizationSourceOption,
  defaultHalcyonResubmitModeOption,
  FieldTypesEnum,
  finicityOption,
  floodElevationCertificateProviderOptions,
  FloodElevationCertificateProviderType,
  getHalcyonSpecificYearOptions,
  halcyonAuthorizationSourceOptions,
  HalcyonBorrowerTargetEnum,
  halcyonBorrowerTargetOptions,
  halcyonFormGroupBy,
  HalcyonMethodEnum,
  halcyonMethodOptions,
  HalcyonMultiYearEnum,
  halcyonResubmitModeOptions,
  halcyonYearModeOptions,
  ReportTypeEnum,
  VendorTypeEnum,
  verificationOfAssetsOptions,
} from 'constants/loanActionEditor';
import { deleteByIndex, setIn } from 'utils/data';

import { selectHalcyonFormPickerOptions } from 'store/loanActions/halcyonFormCatalog';
import { selectHalcyonFormInclusionSchema } from 'store/system/selectors';

import ExpressionField from '../../form/ExpressionField';
import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';
import { wrapperClassname } from './LoanActionItem.constants';
import { DynamicValueInput } from './LoanActionItems';
import LoanActionServiceLinkFields from './LoanActionServiceLinkFields';
import { getAVMReportConfig, groupFieldsFunction } from './LoanActionsGroup.utils';

const halcyonSpecificYearOptions = getHalcyonSpecificYearOptions();

const emptyFloodProductOptions = [];

const LoanActionReportRow = ({
  index,
  rootPath,
  options,
  isDisabled,
  isAutomatedActions,
  isEditAllowed,
  label = '',
  isSingleItem,
  creditReportActionSubtypeOptions,
  floodProductOptions = emptyFloodProductOptions,
}) => {
  const {
    form,
    setInForm,
    setMultipleInForm,
    setValidationErrors,
    validateField,
    validationErrors,
    handleChange,
    staticValidateField,
  } = useFormContext();

  const halcyonFormInclusionSchemaParams = useSelector(selectHalcyonFormInclusionSchema);
  const halcyonFormOptions = useSelector(selectHalcyonFormPickerOptions);

  const fieldPath = `${rootPath}[${index}]`;
  const reportFieldPath = `${fieldPath}.report`;
  const argyleReportTypeFieldPath = `${fieldPath}.argyleReportType`;
  const floodElevationCertificateProviderFieldPath = `${fieldPath}.floodElevationCertificateProvider`;
  const serviceLinkOperationFieldPath = `${fieldPath}.serviceLinkOperation`;
  const serviceLinkProductOverrideFieldPath = `${fieldPath}.serviceLinkProductOverride`;
  const serviceLinkRushFieldPath = `${fieldPath}.serviceLinkRush`;
  const serviceLinkAllowManualSelectionFieldPath = `${fieldPath}.serviceLinkAllowManualSelection`;
  const verificationOfAssetsFieldPath = `${fieldPath}.verificationOfAssets`;
  const creditActionSubtypeFieldPath = `${fieldPath}.creditActionSubtype`;
  const creditBureausFieldPath = `${fieldPath}.creditBureaus`;
  const creditPullTypeFieldPath = `${fieldPath}.creditPullType`;
  const creditAllowManualSelectionFieldPath = `${fieldPath}.creditAllowManualSelection`;
  const avmVendorsFieldPath = `${fieldPath}.vendorOptions.vendorType`;
  const enableCascadeOnHighFsdFieldPath = `${fieldPath}.vendorOptions.enableCascadeOnHighFsd`;
  const orderedCascadeProvidersFieldPath = `${fieldPath}.vendorOptions.orderedCascadeProviders`;

  const halcyonMethodFieldPath = `${fieldPath}.halcyonMethod`;
  const halcyonBaseFormsFieldPath = `${fieldPath}.halcyonBaseForms`;
  const halcyonBaseYearModeFieldPath = `${fieldPath}.halcyonBaseYearMode`;
  const halcyonBaseSpecificYearsFieldPath = `${fieldPath}.halcyonBaseSpecificYears`;
  const halcyonUseConditionalRulesFieldPath = `${fieldPath}.halcyonUseConditionalRules`;
  const halcyonConditionalRulesFieldPath = `${fieldPath}.halcyonConditionalRules`;
  const halcyonBorrowerTargetFieldPath = `${fieldPath}.halcyonBorrowerTarget`;
  const halcyonBorrowerExpressionFieldPath = `${fieldPath}.halcyonBorrowerExpression`;
  const halcyonAllowManualSelectionFieldPath = `${fieldPath}.halcyonAllowManualSelection`;
  const halcyonResubmitModeFieldPath = `${fieldPath}.halcyonResubmitMode`;
  const halcyonAuthorizationSourceFieldPath = `${fieldPath}.halcyonAuthorizationSource`;
  const halcyonUseNonProdCredentialsFieldPath = `${fieldPath}.halcyonUseNonProdCredentials`;
  const halcyonIsPreAuthorizationFieldPath = `${fieldPath}.halcyonIsPreAuthorization`;

  const forms = _.get(form, rootPath);
  const report = _.get(form, reportFieldPath);
  const avmVendorType = _.get(form, avmVendorsFieldPath);
  const creditActionSubtype = _.get(form, creditActionSubtypeFieldPath);
  const creditBureaus = _.get(form, creditBureausFieldPath);
  const creditPullType = _.get(form, creditPullTypeFieldPath);
  const creditAllowManualSelection = _.get(form, creditAllowManualSelectionFieldPath);
  const floodElevationCertificateProvider = _.get(form, floodElevationCertificateProviderFieldPath);
  const isCreditReport = report?.value === ReportTypeEnum.CREDIT;
  const isCreditOrderSubtype =
    isCreditReport && creditOrderActionSubtypes.includes(creditActionSubtype?.value);
  const isAVMReport = report?.value === ReportTypeEnum.AVM;
  const isArgyleVerification = report?.value === ReportTypeEnum.ARGYLE_VERIFICATION;
  const isFloodElevationCertificate = report?.value === ReportTypeEnum.FLOOD_ELEVATION_CERTIFICATE;
  const isServiceLinkProvider =
    floodElevationCertificateProvider?.value === FloodElevationCertificateProviderType.SERVICE_LINK;
  const isVerificationOfAssets = report?.value === ReportTypeEnum.VERIFICATION_OF_ASSETS;
  const isHalcyonTaxTranscript = report?.value === ReportTypeEnum.HALCYON_TAX_TRANSCRIPT;
  const isClearCapital = avmVendorType?.value === VendorTypeEnum.CLEAR_CAPITAL;
  const isCascadeOnHighFsdEnabled = _.get(form, enableCascadeOnHighFsdFieldPath);
  const orderedCascadeProviders = _.get(form, orderedCascadeProvidersFieldPath);

  const halcyonBaseYearMode = _.get(form, halcyonBaseYearModeFieldPath);
  const isHalcyonBaseSpecificYears = halcyonBaseYearMode?.value === HalcyonMultiYearEnum.SPECIFIC;
  const halcyonUseConditionalRules = _.get(form, halcyonUseConditionalRulesFieldPath);
  const halcyonConditionalRules = useMemo(
    () => _.get(form, halcyonConditionalRulesFieldPath) || [],
    [form, halcyonConditionalRulesFieldPath],
  );
  const halcyonBorrowerTarget = _.get(form, halcyonBorrowerTargetFieldPath);
  const isHalcyonBorrowerByExpression =
    halcyonBorrowerTarget?.value === HalcyonBorrowerTargetEnum.BY_EXPRESSION;
  const halcyonBorrowerExpression = _.get(form, halcyonBorrowerExpressionFieldPath) || '';
  const halcyonAllowManualSelection = _.get(form, halcyonAllowManualSelectionFieldPath);
  const halcyonUseNonProdCredentials = _.get(form, halcyonUseNonProdCredentialsFieldPath);
  const halcyonIsPreAuthorization = _.get(form, halcyonIsPreAuthorizationFieldPath);
  const halcyonMethod = _.get(form, halcyonMethodFieldPath);
  const halcyonBorrowerExpressionError = _.get(
    validationErrors,
    halcyonBorrowerExpressionFieldPath,
  );
  const creditBureausError = _.get(validationErrors, creditBureausFieldPath);

  const areFieldsDisabled = isDisabled || !isEditAllowed;
  const isHalcyonPreAuthScopedForms =
    !!halcyonIsPreAuthorization &&
    (halcyonMethod?.value === HalcyonMethodEnum.FORM_8821 ||
      halcyonMethod?.value === HalcyonMethodEnum.COMBINED);
  const areHalcyonBaseFormsHidden =
    !!halcyonIsPreAuthorization && halcyonMethod?.value === HalcyonMethodEnum.IRS_ACCOUNT;

  const avmReportConfig = isAVMReport
    ? getAVMReportConfig({
        fieldPath,
        areFieldsDisabled,
        isClearCapital,
        isCascadeOnHighFsdEnabled,
      })
    : null;

  const onDeleteRow = useCallback(() => {
    const newForms = [...forms];

    newForms.splice(index, 1);

    setInForm(rootPath, newForms);
    setValidationErrors(errors =>
      setIn(errors, rootPath, deleteByIndex(_.get(errors, rootPath, []), index)),
    );
  }, [setInForm, setValidationErrors, index, forms, rootPath]);

  const onChangeReport = useCallback(() => {
    setMultipleInForm({
      [creditActionSubtypeFieldPath]: null,
      [creditBureausFieldPath]: null,
      [creditPullTypeFieldPath]: null,
      [creditAllowManualSelectionFieldPath]: false,
      [avmVendorsFieldPath]: null,
      [argyleReportTypeFieldPath]: null,
      [floodElevationCertificateProviderFieldPath]: null,
      [serviceLinkOperationFieldPath]: null,
      [serviceLinkProductOverrideFieldPath]: null,
      [serviceLinkRushFieldPath]: false,
      [serviceLinkAllowManualSelectionFieldPath]: false,
      [halcyonMethodFieldPath]: null,
      [halcyonBaseFormsFieldPath]: [],
      [halcyonBaseYearModeFieldPath]: null,
      [halcyonBaseSpecificYearsFieldPath]: [],
      [halcyonUseConditionalRulesFieldPath]: false,
      [halcyonConditionalRulesFieldPath]: [],
      [halcyonBorrowerTargetFieldPath]: null,
      [halcyonBorrowerExpressionFieldPath]: '',
      [halcyonAllowManualSelectionFieldPath]: false,
      [halcyonResubmitModeFieldPath]: defaultHalcyonResubmitModeOption,
      [halcyonAuthorizationSourceFieldPath]: defaultHalcyonAuthorizationSourceOption,
      [halcyonUseNonProdCredentialsFieldPath]: false,
      [halcyonIsPreAuthorizationFieldPath]: false,
    });
  }, [
    setMultipleInForm,
    creditActionSubtypeFieldPath,
    creditBureausFieldPath,
    creditPullTypeFieldPath,
    creditAllowManualSelectionFieldPath,
    avmVendorsFieldPath,
    argyleReportTypeFieldPath,
    floodElevationCertificateProviderFieldPath,
    serviceLinkOperationFieldPath,
    serviceLinkProductOverrideFieldPath,
    serviceLinkRushFieldPath,
    serviceLinkAllowManualSelectionFieldPath,
    halcyonMethodFieldPath,
    halcyonBaseFormsFieldPath,
    halcyonBaseYearModeFieldPath,
    halcyonBaseSpecificYearsFieldPath,
    halcyonUseConditionalRulesFieldPath,
    halcyonConditionalRulesFieldPath,
    halcyonBorrowerTargetFieldPath,
    halcyonBorrowerExpressionFieldPath,
    halcyonAllowManualSelectionFieldPath,
    halcyonResubmitModeFieldPath,
    halcyonAuthorizationSourceFieldPath,
    halcyonUseNonProdCredentialsFieldPath,
    halcyonIsPreAuthorizationFieldPath,
  ]);

  const onChangeFloodElevationCertificateProvider = useCallback(() => {
    setMultipleInForm({
      [serviceLinkOperationFieldPath]: null,
      [serviceLinkProductOverrideFieldPath]: null,
      [serviceLinkRushFieldPath]: false,
      [serviceLinkAllowManualSelectionFieldPath]: false,
    });
  }, [
    setMultipleInForm,
    serviceLinkOperationFieldPath,
    serviceLinkProductOverrideFieldPath,
    serviceLinkRushFieldPath,
    serviceLinkAllowManualSelectionFieldPath,
  ]);

  const onToggleConditionalRules = useCallback(
    e => {
      const isChecked = e.target.checked;

      setInForm(halcyonUseConditionalRulesFieldPath, isChecked);

      if (!isChecked) {
        setInForm(halcyonConditionalRulesFieldPath, []);
      }
    },
    [setInForm, halcyonUseConditionalRulesFieldPath, halcyonConditionalRulesFieldPath],
  );

  const onAddConditionalRule = useCallback(() => {
    setInForm(halcyonConditionalRulesFieldPath, [
      ...halcyonConditionalRules,
      createEmptyFormYearRule(),
    ]);
  }, [setInForm, halcyonConditionalRulesFieldPath, halcyonConditionalRules]);

  const onToggleBureauHandlers = useMemo(
    () =>
      creditBureauFields.reduce((acc, { key }) => {
        acc[key] = e => {
          setInForm(`${creditBureausFieldPath}.${key}`, e.target.checked);
          setValidationErrors(errors => setIn(errors, creditBureausFieldPath, ''));
        };

        return acc;
      }, {}),
    [setInForm, setValidationErrors, creditBureausFieldPath],
  );

  const onToggleCreditAllowManualSelection = useCallback(
    e => setInForm(creditAllowManualSelectionFieldPath, e.target.checked),
    [setInForm, creditAllowManualSelectionFieldPath],
  );

  const onToggleAllowManualSelection = useCallback(
    e => setInForm(halcyonAllowManualSelectionFieldPath, e.target.checked),
    [setInForm, halcyonAllowManualSelectionFieldPath],
  );

  const onToggleUseNonProdCredentials = useCallback(
    e => setInForm(halcyonUseNonProdCredentialsFieldPath, e.target.checked),
    [setInForm, halcyonUseNonProdCredentialsFieldPath],
  );

  const onToggleIsPreAuthorization = useCallback(
    e => setInForm(halcyonIsPreAuthorizationFieldPath, e.target.checked),
    [setInForm, halcyonIsPreAuthorizationFieldPath],
  );

  const removeConditionalRuleHandlers = useMemo(
    () =>
      halcyonConditionalRules.map((__unusedRule, ruleIndex) => () => {
        const newRules = halcyonConditionalRules.filter((__, i) => i !== ruleIndex);

        setInForm(halcyonConditionalRulesFieldPath, newRules);
        setValidationErrors(errors => {
          const ruleErrors = _.get(errors, halcyonConditionalRulesFieldPath, []);
          const newRuleErrors = deleteByIndex(ruleErrors, ruleIndex);

          return setIn(errors, halcyonConditionalRulesFieldPath, newRuleErrors);
        });
      }),
    [setInForm, setValidationErrors, halcyonConditionalRulesFieldPath, halcyonConditionalRules],
  );

  useEffect(() => {
    if (report?.value !== ReportTypeEnum.AVM) {
      return;
    }

    if (_.isNil(avmVendorType)) {
      setInForm(avmVendorsFieldPath, avmVendorTypeMap[VendorTypeEnum.CLEAR_CAPITAL]);
      setValidationErrors(errors => setIn(errors, avmVendorsFieldPath, ''));
    }

    if (!orderedCascadeProviders) {
      setInForm(orderedCascadeProvidersFieldPath, []);
    }
  }, [
    report,
    avmVendorType,
    orderedCascadeProviders,
    avmVendorsFieldPath,
    orderedCascadeProvidersFieldPath,
    setValidationErrors,
    setInForm,
  ]);

  useEffect(() => {
    if (!isVerificationOfAssets) {
      return;
    }

    setInForm(verificationOfAssetsFieldPath, finicityOption);
  }, [isVerificationOfAssets, setInForm, verificationOfAssetsFieldPath]);

  useEffect(() => {
    if (!isCreditOrderSubtype) {
      return;
    }

    if (!creditBureaus) {
      setInForm(creditBureausFieldPath, createDefaultCreditBureaus());
    }

    if (!creditPullType) {
      setInForm(creditPullTypeFieldPath, defaultCreditPullTypeOption);
    }
  }, [
    isCreditOrderSubtype,
    creditBureaus,
    creditPullType,
    creditBureausFieldPath,
    creditPullTypeFieldPath,
    setInForm,
  ]);

  return (
    <div className={wrapperClassname}>
      <div className="flex">
        <DynamicValueInput
          required
          className="grow"
          disabled={areFieldsDisabled}
          groupBy={groupFieldsFunction}
          label={label}
          name={reportFieldPath}
          options={options}
          type={FieldTypesEnum.SELECT}
          onChange={onChangeReport}
        />
        {isEditAllowed && !isSingleItem && (
          <ButtonWithTooltip
            data-testid={loanManagementTestIds.getDeleteRowButtonId(fieldPath)}
            disabled={isDisabled}
            icon={DeleteIcon}
            size="medium"
            spanContainerClassName="flex items-center"
            title="Delete row"
            onClick={onDeleteRow}
          />
        )}
      </div>

      {isCreditReport && (
        <DynamicValueInput
          required
          disabled={areFieldsDisabled}
          label="Action Subtype"
          name={creditActionSubtypeFieldPath}
          options={creditReportActionSubtypeOptions}
          type={FieldTypesEnum.SELECT}
        />
      )}

      {isCreditOrderSubtype && (
        <>
          <div className="flex">
            {creditBureauFields.map(({ name, key }) => (
              <CheckboxCDS
                dataTestId={loanManagementTestIds.getCreditBureauCheckboxId(fieldPath, key)}
                disabled={areFieldsDisabled}
                error={!!creditBureausError}
                key={key}
                label={name}
                value={!!creditBureaus?.[key]}
                onChange={onToggleBureauHandlers[key]}
              />
            ))}
          </div>
          {!!creditBureausError && <FormHelperText error>{creditBureausError}</FormHelperText>}
          <DynamicValueInput
            required
            disabled={areFieldsDisabled}
            label="Pull Type"
            name={creditPullTypeFieldPath}
            options={creditPullTypeOptions}
            type={FieldTypesEnum.SELECT}
          />
          {!isAutomatedActions && (
            <FormControlLabel
              control={
                <Switch
                  checked={!!creditAllowManualSelection}
                  disabled={areFieldsDisabled}
                  onChange={onToggleCreditAllowManualSelection}
                />
              }
              data-testid={loanManagementTestIds.getCreditManualSelectionSwitchId(fieldPath)}
              label="Allow manual bureau selection at execution time"
            />
          )}
        </>
      )}

      {isAVMReport && (
        <FormGenerator
          isNestedForm
          config={avmReportConfig}
          form={form}
          handleChange={handleChange}
          numberOfColumns={1}
          setInForm={setInForm}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      )}

      {isArgyleVerification && isAutomatedActions && (
        <DynamicValueInput
          required
          disabled={areFieldsDisabled}
          label="Report Type"
          name={argyleReportTypeFieldPath}
          options={argyleReportTypeOptions}
          type={FieldTypesEnum.SELECT}
        />
      )}

      {isFloodElevationCertificate && (
        <>
          <DynamicValueInput
            required
            disabled={areFieldsDisabled}
            label="Vendor"
            name={floodElevationCertificateProviderFieldPath}
            options={floodElevationCertificateProviderOptions}
            type={FieldTypesEnum.SELECT}
            onChange={onChangeFloodElevationCertificateProvider}
          />

          {isServiceLinkProvider && (
            <LoanActionServiceLinkFields
              disabled={areFieldsDisabled}
              fieldPath={fieldPath}
              floodProductOptions={floodProductOptions}
              isAutomatedActions={isAutomatedActions}
            />
          )}
        </>
      )}

      {isVerificationOfAssets && (
        <DynamicValueInput
          required
          disabled={areFieldsDisabled}
          label="Vendor"
          name={verificationOfAssetsFieldPath}
          options={verificationOfAssetsOptions}
          type={FieldTypesEnum.SELECT}
        />
      )}

      {isHalcyonTaxTranscript && (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={!!halcyonUseNonProdCredentials}
                disabled={areFieldsDisabled}
                onChange={onToggleUseNonProdCredentials}
              />
            }
            data-testid={loanManagementTestIds.getHalcyonNonProdCredentialsSwitchId(fieldPath)}
            label="Use non-production Halcyon credentials"
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!halcyonIsPreAuthorization}
                disabled={areFieldsDisabled}
                onChange={onToggleIsPreAuthorization}
              />
            }
            data-testid={loanManagementTestIds.getHalcyonPreAuthorizationSwitchId(fieldPath)}
            label="Pre-Authorization (authorize without pulling transcripts)"
          />
          {!isAutomatedActions && (
            <FormControlLabel
              control={
                <Switch
                  checked={!!halcyonAllowManualSelection}
                  disabled={areFieldsDisabled}
                  onChange={onToggleAllowManualSelection}
                />
              }
              data-testid={loanManagementTestIds.getHalcyonManualSelectionSwitchId(fieldPath)}
              label="Allow manual selection at execution time"
            />
          )}
          {(!halcyonAllowManualSelection || isAutomatedActions) && (
            <>
              <DynamicValueInput
                required
                disabled={areFieldsDisabled}
                label="Resubmit Mode"
                name={halcyonResubmitModeFieldPath}
                options={halcyonResubmitModeOptions}
                type={FieldTypesEnum.SELECT}
              />
              <DynamicValueInput
                required
                disabled={areFieldsDisabled}
                label="Authorization Source"
                name={halcyonAuthorizationSourceFieldPath}
                options={halcyonAuthorizationSourceOptions}
                type={FieldTypesEnum.SELECT}
              />
              <DynamicValueInput
                required
                disabled={areFieldsDisabled}
                label="Transcript Method"
                name={halcyonMethodFieldPath}
                options={halcyonMethodOptions}
                type={FieldTypesEnum.SELECT}
              />
              {!areHalcyonBaseFormsHidden && (
                <>
                  <DynamicValueInput
                    required
                    disabled={areFieldsDisabled}
                    groupBy={halcyonFormGroupBy}
                    label={isHalcyonPreAuthScopedForms ? 'Pre-Authorized Forms' : 'Base Forms'}
                    name={halcyonBaseFormsFieldPath}
                    options={halcyonFormOptions}
                    type={FieldTypesEnum.MULTISELECT}
                  />
                  <DynamicValueInput
                    required
                    disabled={areFieldsDisabled}
                    label="Year Mode"
                    name={halcyonBaseYearModeFieldPath}
                    options={halcyonYearModeOptions}
                    type={FieldTypesEnum.SELECT}
                  />
                  {isHalcyonBaseSpecificYears && (
                    <DynamicValueInput
                      required
                      disabled={areFieldsDisabled}
                      label="Specific Years"
                      name={halcyonBaseSpecificYearsFieldPath}
                      options={halcyonSpecificYearOptions}
                      type={FieldTypesEnum.MULTISELECT}
                    />
                  )}

                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!halcyonUseConditionalRules}
                        disabled={areFieldsDisabled}
                        onChange={onToggleConditionalRules}
                      />
                    }
                    data-testid={loanManagementTestIds.getHalcyonConditionalRulesSwitchId(
                      fieldPath,
                    )}
                    label="Add conditional form rules"
                  />
                </>
              )}

              {!areHalcyonBaseFormsHidden &&
                !!halcyonUseConditionalRules &&
                halcyonConditionalRules.map((rule, ruleIndex) => {
                  const rulePath = `${halcyonConditionalRulesFieldPath}[${ruleIndex}]`;
                  const ruleExpressionPath = `${rulePath}.expression`;
                  const ruleFormsPath = `${rulePath}.forms`;
                  const ruleYearModePath = `${rulePath}.yearMode`;
                  const ruleSpecificYearsPath = `${rulePath}.specificYears`;
                  const ruleYearMode = _.get(form, ruleYearModePath);
                  const isRuleSpecificYears = ruleYearMode?.value === HalcyonMultiYearEnum.SPECIFIC;
                  const ruleExpressionValue = _.get(form, ruleExpressionPath) || '';
                  const ruleExpressionError = _.get(validationErrors, ruleExpressionPath);

                  return (
                    <Box className="mb-16 ml-16 rounded-8 border p-12" key={rulePath}>
                      <Box className="mb-8 flex items-center justify-between">
                        <span className="font-semibold">Conditional Rule {ruleIndex + 1}</span>
                        <ButtonWithTooltip
                          data-testid={loanManagementTestIds.getHalcyonRuleDeleteButtonId(rulePath)}
                          disabled={areFieldsDisabled}
                          icon={DeleteIcon}
                          size="small"
                          title="Remove rule"
                          onClick={removeConditionalRuleHandlers[ruleIndex]}
                        />
                      </Box>
                      <ExpressionField
                        isOnlyBooleanAllowed
                        required
                        className="mb-8"
                        dataTestId={`halcyon-rule-expression-${ruleIndex}`}
                        disabled={areFieldsDisabled}
                        error={!!ruleExpressionError}
                        handleChange={handleChange}
                        helperText={ruleExpressionError}
                        id={`halcyon-rule-expression-${ruleIndex}`}
                        label="Condition Expression"
                        name={ruleExpressionPath}
                        schemaParams={halcyonFormInclusionSchemaParams}
                        setInForm={setInForm}
                        staticValidateField={staticValidateField}
                        validateField={validateField}
                        value={ruleExpressionValue}
                      />
                      <DynamicValueInput
                        required
                        disabled={areFieldsDisabled}
                        groupBy={halcyonFormGroupBy}
                        label="THEN also order (Forms)"
                        name={ruleFormsPath}
                        options={halcyonFormOptions}
                        type={FieldTypesEnum.MULTISELECT}
                      />
                      <DynamicValueInput
                        required
                        disabled={areFieldsDisabled}
                        label="Year Mode"
                        name={ruleYearModePath}
                        options={halcyonYearModeOptions}
                        type={FieldTypesEnum.SELECT}
                      />
                      {isRuleSpecificYears && (
                        <DynamicValueInput
                          required
                          disabled={areFieldsDisabled}
                          label="Specific Years"
                          name={ruleSpecificYearsPath}
                          options={halcyonSpecificYearOptions}
                          type={FieldTypesEnum.MULTISELECT}
                        />
                      )}
                    </Box>
                  );
                })}

              {!areHalcyonBaseFormsHidden && !!halcyonUseConditionalRules && (
                <Box className="ml-16 mb-8">
                  <ButtonWithTooltip
                    data-testid={loanManagementTestIds.getHalcyonAddRuleButtonId(fieldPath)}
                    disabled={areFieldsDisabled}
                    icon={PlusIcon}
                    size="small"
                    title="Add Conditional Rule"
                    onClick={onAddConditionalRule}
                  />
                </Box>
              )}

              <DynamicValueInput
                required
                disabled={areFieldsDisabled}
                label="Borrower Target"
                name={halcyonBorrowerTargetFieldPath}
                options={halcyonBorrowerTargetOptions}
                type={FieldTypesEnum.SELECT}
              />
              {isHalcyonBorrowerByExpression && (
                <ExpressionField
                  isOnlyBooleanAllowed
                  required
                  className="mt-8 mb-24"
                  dataTestId={loanManagementTestIds.CONTENT_VIEW_HALCYON_BORROWER_EXPRESSION_INPUT}
                  disabled={areFieldsDisabled}
                  error={!!halcyonBorrowerExpressionError}
                  handleChange={handleChange}
                  helperText={halcyonBorrowerExpressionError}
                  id="halcyon-borrower-expression"
                  label="Borrower Expression"
                  name={halcyonBorrowerExpressionFieldPath}
                  schemaParams={halcyonFormInclusionSchemaParams}
                  setInForm={setInForm}
                  staticValidateField={staticValidateField}
                  validateField={validateField}
                  value={halcyonBorrowerExpression}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

LoanActionReportRow.propTypes = {
  rootPath: PropTypes.string.isRequired,
  isSingleItem: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  options: PropTypes.array.isRequired,
  creditReportActionSubtypeOptions: PropTypes.array.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isAutomatedActions: PropTypes.bool.isRequired,
  label: PropTypes.string,
  floodProductOptions: PropTypes.array,
};

export default memo(LoanActionReportRow);
